import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiException, type ApiError, type ApiErrorCode, type ApiSuccess } from '@/types/api';
import { env } from '@/config/env';

/**
 * The single typed HTTP client for the Glanz Luxe API.
 *
 * BUILD-PLAN §17 requires that authentication credentials are not kept in
 * unsafe browser storage. This client therefore uses Sanctum's cookie-based
 * session flow (`withCredentials`) rather than storing a bearer token in
 * localStorage, where any XSS payload could read it. No token is ever held
 * in JavaScript memory or persisted by this module.
 */
const instance: AxiosInstance = axios.create({
  baseURL: `${env.apiUrl}/api/v1`,
  withCredentials: true,
  withXSRFToken: true,
  timeout: 20_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Fetches the CSRF cookie Sanctum requires before any state-changing request.
 *
 * Called lazily and memoised: issuing it on every mutation would double the
 * request count for no benefit, but issuing it never breaks all writes.
 */
let csrfPromise: Promise<void> | null = null;

export function ensureCsrfCookie(): Promise<void> {
  csrfPromise ??= axios
    .get(`${env.apiUrl}/sanctum/csrf-cookie`, {
      withCredentials: true,

      /*
       | A timeout is not optional here.
       |
       | Every mutation awaits this in a request interceptor, so a CSRF call
       | that never settles freezes EVERY write in the application — and does
       | it silently: no error, no rejection, no loading state ever ending.
       | A form simply stops responding and the customer is left pressing a
       | button that will never do anything.
       |
       | Bounded, so a stuck call becomes a visible failure the UI can report
       | and the customer can retry.
       */
      timeout: 15000,
    })
    .then(() => undefined)
    .catch((error: unknown) => {
      // Reset so a transient failure does not permanently block writes.
      csrfPromise = null;
      throw error;
    });

  return csrfPromise;
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (MUTATING_METHODS.has((config.method ?? 'get').toLowerCase())) {
    await ensureCsrfCookie();
  }

  return config;
});

/**
 * Normalises every failure into an ApiException.
 *
 * Without this, each caller would have to distinguish "the backend returned
 * a structured error", "the network is down" and "the request timed out".
 * Collapsing them here is what lets the UI render an intentional state for
 * each case (§41 offline / error / unauthorized states).
 */
instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiError>) => {
    // No response at all: DNS failure, offline, CORS, or timeout.
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED';

      return Promise.reject(
        new ApiException(
          'NETWORK_ERROR',
          isTimeout
            ? 'The request took too long. Please check your connection and try again.'
            : 'We could not reach Glanz Luxe. Please check your connection.',
          0,
        ),
      );
    }

    const { status, data } = error.response;

    // A structured envelope from our own API.
    if (data && typeof data === 'object' && 'code' in data) {
      return Promise.reject(
        new ApiException(
          data.code as ApiErrorCode,
          data.message || 'Unable to complete the request.',
          status,
          data.errors ?? {},
        ),
      );
    }

    // A response from something that is not our API (proxy, load balancer).
    return Promise.reject(
      new ApiException('REQUEST_FAILED', 'Unable to complete the request.', status),
    );
  },
);

/** Unwraps the §42 success envelope so callers receive `data` directly. */
async function unwrap<T>(promise: Promise<AxiosResponse<ApiSuccess<T>>>): Promise<T> {
  const response = await promise;

  return response.data.data;
}

export const api = {
  /**
   * @param config Extra axios options — used for per-request headers such as
   *               the checkout token, which authorises a guest to pay for the
   *               order they just placed (§29). Deliberately narrow: this is
   *               not a general escape hatch for bypassing the client.
   */
  get: <T>(
    url: string,
    params?: Record<string, unknown>,
    config?: { headers?: Record<string, string> },
  ): Promise<T> => unwrap<T>(instance.get(url, { params, ...config })),

  /**
   * Returns the whole success envelope rather than just `data`.
   *
   * Needed for paginated endpoints, where `meta` carries the page state. A
   * caller that only received `data` would have to make a second request, or
   * guess whether more pages exist.
   */
  getRaw: async <T>(
    url: string,
    params?: Record<string, unknown>,
  ): Promise<ApiSuccess<T>> => {
    const response = await instance.get<ApiSuccess<T>>(url, { params });

    return response.data;
  },

  post: <T>(
    url: string,
    body?: unknown,
    config?: { headers?: Record<string, string> },
  ): Promise<T> => unwrap<T>(instance.post(url, body, config)),

  put: <T>(url: string, body?: unknown): Promise<T> => unwrap<T>(instance.put(url, body)),

  patch: <T>(url: string, body?: unknown): Promise<T> => unwrap<T>(instance.patch(url, body)),

  delete: <T>(url: string): Promise<T> => unwrap<T>(instance.delete(url)),
};

export { instance as httpClient };
